import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { formatDateToRelativeOrShortDate } from "../Helpers/utils";
import { useGlobalState } from "../contexts/GlobalStateContext";
import useScrollLockOnMount from "../Helpers/utils";
import Post from "./Post";
import RepliesModal from "./RepliesModal";

const CommentsModal = ({
  post,
  noOfComments,
  onCommentCountChange,
  onClose,
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true); // Overall loading for initial comments
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false); // For infinite scroll of parent comments
  const [currentPage, setCurrentPage] = useState(1);

  // --- State for Replies ---
  const [repliesLoadingState, setRepliesLoadingState] = useState({}); // Tracks loading state for each comment's replies e.g. { commentId1: true, commentId2: false }
  const [lastReplyIdMap, setLastReplyIdMap] = useState({});
  const [hasMoreRepliesMap, setHasMoreRepliesMap] = useState({}); // Tracks if more replies are available for a comment e.g. { commentId1: true }
  // --- End State for Replies ---

  const [newComment, setNewComment] = useState("");
  const [replyInputs, setReplyInputs] = useState({});
  const [openReplyForms, setOpenReplyForms] = useState({});
  // const [newReplies, setNewReplies] = useState({}); // This can likely be removed if replies are directly added
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [replyToDelete, setReplyToDelete] = useState(null);
  const [parentCommentID, setParentCommentID] = useState(null);
  const {
    globalState,
    setShowLogins,
    logOut,
    resetGlobalState,
    setSessionExpired,
    setFullScreenPost
  } = useGlobalState();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const inputRef = useRef(null);
  const commentRef = useRef(null);
  const userId = globalState?.user?.user_id;

  const profileImage = globalState.isAuthenticated
    ? `${globalState.user.user_id}.jpg`
    : "default.jpg";

  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  useEffect(() => {
    fetchComments(post.uuid, 1); // Renamed for clarity
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [post.uuid]);

  // NEW: Programmatically focus and blur on mount to prime the browser
  useEffect(() => {
    if (!isMobile) return; // Only for mobile devices
    const inputElement = commentRef.current;

    if (inputElement) {
      // Small delay to ensure the modal is fully rendered before trying to focus
      const timer = setTimeout(() => {
        console.log("Programmatic focus/blur on mount to prime keyboard.");
        inputElement.focus();
        // A very brief delay before blurring, just enough for the browser to register focus
        setTimeout(() => {
          inputElement.blur();
        }, 10); // Very short delay before blur
      }, 50); // Small initial delay after mount

      return () => clearTimeout(timer);
    }
  }, [isMobile]); // Run only once on mount for mobile

  // Load initial comments or more parent comments
  const fetchComments = async (currentUuid, page) => {
    if (isLoadingMoreComments || (page > 1 && !hasMoreComments)) return; // Prevent multiple loads if already loading or no more parent comments
    setIsLoadingMoreComments(true);
    if (page === 1) setLoading(true); // Only set overall loading for the first page

    try {
      const response = await fetch(
        `${baseUrl}/php/fetch_comments.php?uuid=${currentUuid}&page=${page}`
      );
      const data = await response.json();

      if (data.success) {
        if (data.comments.length === 0) {
          setHasMoreComments(false);
          if (page === 1) setComments([]);
        } else {
          setComments((prev) =>
            page === 1 ? data.comments : [...prev, ...data.comments]
          );

          const newLastReplyIdMapUpdates = {}; // For lastReplyIdMap
          const newHasMoreRepliesMapUpdates = {};

          data.comments.forEach((comment) => {
            if (comment.replies && comment.replies.length > 0) {
              // If initial replies are provided with the parent comment
              newLastReplyIdMapUpdates[comment.comment_id] =
                comment.replies[comment.replies.length - 1].comment_id;
              newHasMoreRepliesMapUpdates[comment.comment_id] =
                (parseInt(comment.noOfReplies) || 0) > comment.replies.length;
            } else {
              // No initial replies, or replies array is empty
              newLastReplyIdMapUpdates[comment.comment_id] = null; // Indicates no replies loaded yet for this comment
              newHasMoreRepliesMapUpdates[comment.comment_id] =
                (parseInt(comment.noOfReplies) || 0) > 0;
            }
          });

          setLastReplyIdMap((prev) => ({
            ...prev,
            ...newLastReplyIdMapUpdates,
          })); // New state
          setHasMoreRepliesMap((prev) => ({
            ...prev,
            ...newHasMoreRepliesMapUpdates,
          }));
          // setRepliesPageMap is no longer needed
        }
      } else {
        setHasMoreComments(false);
        if (page === 1)
          console.error("Failed to fetch initial comments:", data.message);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setHasMoreComments(false); // Assume no more on error
    } finally {
      if (page === 1) setLoading(false);
      setIsLoadingMoreComments(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollTop + clientHeight >= scrollHeight - 100 && // Increased threshold slightly
      !isLoadingMoreComments &&
      hasMoreComments
    ) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchComments(post.uuid, nextPage);
    }
  };

  const postComment = async () => {
    if (!globalState.isAuthenticated) {
      setShowLogins(true);
      return;
    }
    if (newComment.trim() === "") return;
    const currentTimestamp = new Date().toISOString();

    // Optimistic UI update preparation
    const tempId = `temp-${Date.now()}`;
    const newCommentObjOptimistic = {
      comment_id: tempId, // Temporary ID
      user_id: globalState.user.user_id,
      username: globalState.user.username,
      fullName: globalState.user.fullName,
      content: newComment,
      created_at: currentTimestamp,
      noOfReplies: 0,
      replies: [],
    };

    setComments((prev) => [newCommentObjOptimistic, ...prev]);
    onCommentCountChange(noOfComments + 1);
    const originalNewComment = newComment;
    setNewComment("");

    try {
      const response = await fetch(`${baseUrl}/php/postComment.php`, {
        method: "POST",
        body: new URLSearchParams({
          uuid: post.uuid,
          comment: originalNewComment, // Use the saved comment
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          setSessionExpired(true);
          return;
        }

        throw new Error(data.message || "Unknown error occurred");
      }

      if (data.status === "success" && data.comment) {
        // Update the optimistic comment with actual data from server
        setComments((prev) =>
          prev.map(
            (c) =>
              c.comment_id === tempId ? { ...data.comment, replies: [] } : c // Ensure replies array exists
          )
        );
        const finalCommentId = data.comment.comment_id; // Use the ID from server response

        // Initialize lastReplyIdMap for the new comment
        setLastReplyIdMap((prev) => ({
          ...prev,
          [finalCommentId]: null, // No replies loaded yet
        }));
        setHasMoreRepliesMap((prev) => ({
          ...prev,
          [finalCommentId]: (parseInt(data.comment.noOfReplies) || 0) > 0, // Based on initial noOfReplies for the new comment
        }));
      } else {
        console.error("Failed to post comment", data.message);
        // Rollback optimistic update
        setComments((prev) => prev.filter((c) => c.comment_id !== tempId));
        onCommentCountChange(noOfComments); // Decrement count back
        setNewComment(originalNewComment); // Restore textarea
        alert("Failed to post comment: " + (data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      // Rollback optimistic update
      setComments((prev) => prev.filter((c) => c.comment_id !== tempId));
      onCommentCountChange(noOfComments);
      setNewComment(originalNewComment);
      alert("Something went wrong while posting the comment.");
    }
  };

  const postReply = async (commentId) => {
    if (!globalState.isAuthenticated) {
      setShowLogins(true);
      return;
    }
    const content = replyInputs[commentId]?.trim();
    if (!content) return;

    const currentTimestamp = new Date().toISOString();
    const tempReplyId = `temp-reply-${Date.now()}`;

    // Optimistic UI Update
    const newReplyObjOptimistic = {
      comment_id: tempReplyId, // Temporary ID for the reply
      parent_comment_id: commentId,
      user_id: globalState.user.user_id,
      username: globalState.user.username,
      fullName: globalState.user.fullName,
      content: content,
      created_at: currentTimestamp,
    };

    setReplyInputs((prev) => ({ ...prev, [commentId]: "" }));
    setOpenReplyForms((prev) => ({ ...prev, [commentId]: false })); // Optional: close form after optimistic update

    setComments((prevComments) =>
      prevComments.map((comment) => {
        if (comment.comment_id === commentId) {
          return {
            ...comment,
            replies: [newReplyObjOptimistic, ...(comment.replies || [])],
            noOfReplies: (parseInt(comment.noOfReplies) || 0) + 1,
          };
        }
        return comment;
      })
    );
    const originalReplyInput = replyInputs[commentId];

    try {
      const response = await fetch(`${baseUrl}/php/reply_comment.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment_id: commentId, uuid: post.uuid, content }),
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          setSessionExpired(true);
          return;
        }

        throw new Error(data.message || "Unknown error occurred");
      }

      if (data.status === "success" && data.reply) {
        setComments((prevComments) =>
          prevComments.map((comment) => {
            if (comment.comment_id === commentId) {
              return {
                ...comment,
                replies: comment.replies.map((r) =>
                  r.comment_id === tempReplyId ? data.reply : r
                ),
                // noOfReplies is already incremented optimistically
              };
            }
            return comment;
          })
        );
        setOpenReplyForms((prev) => ({ ...prev, [commentId]: false })); // Close on actual success
      } else {
        console.error("Failed to post reply", data.message);
        // Rollback optimistic update
        setComments((prevComments) =>
          prevComments.map((comment) => {
            if (comment.comment_id === commentId) {
              return {
                ...comment,
                replies: (comment.replies || []).filter(
                  (r) => r.comment_id !== tempReplyId
                ),
                noOfReplies: (parseInt(comment.noOfReplies) || 1) - 1,
              };
            }
            return comment;
          })
        );
        setReplyInputs((prev) => ({
          ...prev,
          [commentId]: originalReplyInput,
        }));
        alert("Failed to post reply: " + (data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error posting reply:", error);
      // Rollback optimistic update
      setComments((prevComments) =>
        prevComments.map((comment) => {
          if (comment.comment_id === commentId) {
            return {
              ...comment,
              replies: (comment.replies || []).filter(
                (r) => r.comment_id !== tempReplyId
              ),
              noOfReplies: (parseInt(comment.noOfReplies) || 1) - 1,
            };
          }
          return comment;
        })
      );
      setReplyInputs((prev) => ({ ...prev, [commentId]: originalReplyInput }));
      alert("Something went wrong while posting the reply.");
    }
  };

  // --- Load More Replies Function ---
  const loadReplies = async (parentCommentId) => {
    const lastSeenReplyId = lastReplyIdMap[parentCommentId] || null; // Get ID of the last reply shown, or null if first fetch

    if (
      repliesLoadingState[parentCommentId] ||
      !hasMoreRepliesMap[parentCommentId]
    ) {
      return;
    }

    setRepliesLoadingState((prev) => ({ ...prev, [parentCommentId]: true }));

    try {
      let fetchUrl = `${baseUrl}/php/fetch_replies.php?comment_id=${parentCommentId}`;
      if (lastSeenReplyId) {
        fetchUrl += `&after_reply_id=${lastSeenReplyId}`;
      }
      // You might want to add a limit, e.g., &limit=5, if your backend supports it
      // fetchUrl += `&limit=5`;

      const response = await fetch(fetchUrl);
      const data = await response.json();

      if (data.success && data.replies) {
        if (data.replies.length > 0) {
          // Update the last seen reply ID for this parent comment
          const newLastReplyIdForThisParent =
            data.replies[data.replies.length - 1].comment_id;
          setLastReplyIdMap((prev) => ({
            ...prev,
            [parentCommentId]: newLastReplyIdForThisParent,
          }));

          setComments((prevComments) =>
            prevComments.map((comment) => {
              if (comment.comment_id === parentCommentId) {
                const existingReplyIds = new Set(
                  (comment.replies || []).map((r) => r.comment_id)
                );
                const newUniqueReplies = data.replies.filter(
                  (reply) => !existingReplyIds.has(reply.comment_id)
                );
                return {
                  ...comment,
                  replies: [...(comment.replies || []), ...newUniqueReplies],
                };
              }
              return comment;
            })
          );
        }
        // Update hasMoreRepliesMap based on server's indication
        setHasMoreRepliesMap((prev) => ({
          ...prev,
          [parentCommentId]: data.hasMoreReplies || false,
        }));
      } else {
        console.error("Failed to fetch replies:", data.message);
        setHasMoreRepliesMap((prev) => ({ ...prev, [parentCommentId]: false }));
      }
    } catch (error) {
      console.error("Error fetching replies:", error);
      setHasMoreRepliesMap((prev) => ({ ...prev, [parentCommentId]: false }));
    } finally {
      setRepliesLoadingState((prev) => ({ ...prev, [parentCommentId]: false }));
    }
  };
  // --- End Load More Replies Function ---

  const handleDeleteClick = (commentId) => {
    setCommentToDelete(commentId);
    setReplyToDelete(null);
    setShowConfirmDialog(true);
  };

  const handleDeleteReplyClick = (parentCommentId, replyId) => {
    setCommentToDelete(parentCommentId);
    setReplyToDelete(replyId);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    const currentParentCommentId = commentToDelete;
    const currentReplyIdToDelete = replyToDelete;
    const itemToDeleteOnBackendId = currentReplyIdToDelete
      ? currentReplyIdToDelete
      : currentParentCommentId;

    if (!itemToDeleteOnBackendId) {
      console.error("No item ID specified for deletion.");
      setShowConfirmDialog(false);
      setCommentToDelete(null);
      setReplyToDelete(null);
      return;
    }

    // --- Optimistic UI Update for Delete ---
    let originalCommentsState = JSON.parse(JSON.stringify(comments)); // Deep copy for rollback
    let originalCommentCount = noOfComments;

    if (currentReplyIdToDelete) {
      // Deleting a reply
      setComments((prevComments) =>
        prevComments.map((comment) => {
          if (comment.comment_id === currentParentCommentId) {
            return {
              ...comment,
              replies: comment.replies.filter(
                (reply) => reply.comment_id !== currentReplyIdToDelete
              ),
              noOfReplies: Math.max(
                0,
                (parseInt(comment.noOfReplies) || 0) - 1
              ),
            };
          }
          return comment;
        })
      );
      // Note: onCommentCountChange was called in original code for reply deletion.
      // This implies noOfComments might track total interactions, not just top-level.
      // If noOfComments is ONLY for top-level, this line should be removed for reply deletion.
      // onCommentCountChange(noOfComments - 1); // Preserving original logic's apparent intent
    } else {
      // Deleting a main comment
      setComments((prevComments) =>
        prevComments.filter((c) => c.comment_id !== currentParentCommentId)
      );
      onCommentCountChange(noOfComments - 1);
    }
    // --- End Optimistic UI Update ---

    setShowConfirmDialog(false); // Close dialog immediately

    try {
      const response = await fetch(`${baseUrl}/php/delete_comment.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment_id: itemToDeleteOnBackendId, uuid: post.uuid }),
      });
      const data = await response.json();

      if (!data.success) {
        // Deletion failed on server
        alert(`Failed to delete: ${data.message || "Unknown server error"}`);
        console.error("Failed to delete item:", data.message);
        // Rollback
        setComments(originalCommentsState);
        onCommentCountChange(originalCommentCount);

        if (data.message === "User not authorized to delete this comment.") {
          logOut();
          resetGlobalState();
        }
      }
      // If successful, optimistic update is already done.
    } catch (error) {
      console.error("Error deleting item:", error);
      alert("An error occurred while trying to delete the item.");
      // Rollback
      setComments(originalCommentsState);
      onCommentCountChange(originalCommentCount);
    } finally {
      // Already set by optimistic update or rollback, but ensure clean state
      setCommentToDelete(null);
      setReplyToDelete(null);
    }
  };
  const [embeddedPosts, setEmbeddedPosts] = useState({});

  useEffect(() => {
    const fetchEmbeddedPosts = async () => {
      const newEmbeddedPosts = {};

      for (const comment of comments) {
        const uuid = extractPostUUID(comment.content);

        if (uuid && !embeddedPosts[uuid]) {
          try {
            const isTextalkPost =
              /https:\/\/(?:textalk\.city\/post\?id=|em\.textalk\.city\/)/i.test(comment.content);

            const isBhutaniPost =
              /https:\/\/(?:bhutani\.cloud\/post\?id=)/i.test(comment.content);

            const assetBaseUrl = isTextalkPost
              ? "https://textalk.city"
              : isBhutaniPost
                ? "https://bhutani.cloud"
                : baseUrl || window.location.origin;

            const endpoint = isTextalkPost
              ? `${baseUrl}/php/getTextalkPost.php?uuid=${encodeURIComponent(uuid)}`
              : `${baseUrl}/php/fetch_post.php`;

            const response = isTextalkPost
              ? await fetch(endpoint)
              : await fetch(endpoint, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ uuid }),
              });

            const data = await response.json();

            if (data.success) {
              const normalizedPost = {
                ...data.post,
                assetBaseUrl,
              };

              newEmbeddedPosts[uuid] = <Post key={uuid} post={normalizedPost} />;
            } else {
              console.warn("Post fetch failed:", data.message);
            }
          } catch (error) {
            console.error("Fetch error:", error);
          }
        }
      }

      if (Object.keys(newEmbeddedPosts).length > 0) {
        setEmbeddedPosts((prev) => ({ ...prev, ...newEmbeddedPosts }));
      }
    };

    fetchEmbeddedPosts();
  }, [comments]); // re-run if comments change

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
    setCommentToDelete(null);
    setReplyToDelete(null);
  };

  const extractPostUUID = (content) => {
    const urlPattern = /https:\/\/(?:bhutani\.cloud\/post\?id=|textalk\.city\/post\?id=|em\.textalk\.city\/)([a-z0-9-]+)/i;
    const match = content.match(urlPattern);
    return match ? match[1] : null;
  };

  useScrollLockOnMount();

  return (
    <>
      {parentCommentID && (
        <RepliesModal
          uuid={post.uuid}
          parentCommentID={parentCommentID}
          onClose={() => {
            setParentCommentID(null);
          }}
        />
      )}
      {/* Confirm Dialog */}
      {showConfirmDialog &&
        createPortal(
          <div
            id="customConfirmDialog"
            style={{ display: "block", zIndex: 1050 }}
          >
            <p id="confirmMessage">
              Are you sure you want to delete this{" "}
              {replyToDelete ? "reply" : "comment"}?
            </p>
            <button id="confirmBtn" onClick={handleConfirmDelete}>
              Yes
            </button>
            <button id="cancelBtn" onClick={handleCancelDelete}>
              No
            </button>
          </div>,
          document.body
        )}

      {/* Comments Modal */}
      {createPortal(
        <div className="comments-modal">
          <div id="comments-modal" style={{ display: "block" }}>
            <div
              className={
                isMobile ? "messages-content-mobile" : "messages-content"
              }
            >
              {/* Header */}
              <div id="messages-header" style={{ backgroundColor: post.theme || "#333" }}>
                <span className="comments-title" style={{ color: post.text_bg || "white" }}>Comments:</span>
                <span className="close" style={{ backgroundColor: post.theme || "#333", color: post.text_bg }} onClick={onClose}>
                  &times;
                </span>
              </div>

              {/* Comments Section */}
              <div id="comments-section" onScroll={handleScroll}>
                {loading ? (
                  <p>Loading...</p>
                ) : comments.length === 0 ? (
                  <p>Be the first one to comment on this.</p>
                ) : (
                  comments.map((comment) => {
                    const uuid = extractPostUUID(comment.content);
                    const embeddedPost = uuid ? embeddedPosts[uuid] : null;

                    return (
                      <div key={comment.comment_id} className="comment-wrapper">
                        <div className="comment-item comment-container">
                          {/* Profile Icon */}
                          <div className="profile-container">
                            <Link
                              to={`/@${encodeURIComponent(comment.username)}`}
                              onClick={() => setFullScreenPost(null)}
                            >
                              <img
                                src={`/profile_pics_icons/${comment.user_id}.jpg`}
                                alt={`${comment.fullName}'s profile`}
                                className="profile_icon"
                                width="33"
                                height="33"
                                onError={(e) =>
                                (e.target.src =
                                  "/profile_pics_icons/default.jpg")
                                }
                              />
                            </Link>
                          </div>

                          {/* Comment Details */}
                          <div className="comment-details">
                            {/* Header */}
                            <div className="comment-header">
                              <Link
                                to={`/@${encodeURIComponent(comment.username)}`}
                                className="comment-author"
                              >
                                {comment.fullName}
                              </Link>
                              <div className="time-delete-container">
                                <span className="comment-time">
                                  {formatDateToRelativeOrShortDate(
                                    comment.created_at
                                  )}
                                </span>
                                {globalState.isAuthenticated &&
                                  comment.user_id ===
                                  globalState.user.user_id && (
                                    <button
                                      className="delete-button"
                                      onClick={() =>
                                        handleDeleteClick(comment.comment_id)
                                      }
                                    >
                                      🗑️
                                    </button>
                                  )}
                              </div>
                            </div>

                            {/* Embedded Post or Normal Content */}
                            {embeddedPost ? (
                              <div className="embedded-post-container">
                                {embeddedPost}
                              </div>
                            ) : (
                              <p className="comment-content">
                                {comment.content}
                              </p>
                            )}

                            {/* Reply Controls */}
                            <div className="reply-controls">
                              <button
                                className="reply-button"
                                onClick={() =>
                                  setOpenReplyForms((prev) => ({
                                    ...prev,
                                    [comment.comment_id]:
                                      !prev[comment.comment_id],
                                  }))
                                }
                              >
                                Reply
                              </button>
                              <span className="reply-count" onClick={() => setParentCommentID(comment.comment_id)}>
                                {parseInt(comment.noOfReplies) || 0}
                                {parseInt(comment.noOfReplies) === 1
                                  ? " reply"
                                  : " replies"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Reply Textarea */}
                        {openReplyForms[comment.comment_id] && (
                          <div className="textarea-container">
                            <textarea
                              ref={inputRef}
                              className="fullTextbox"
                              placeholder="Write a reply..."
                              rows="1"
                              style={{ width: "84%", margin: "5px 10px" }}
                              value={replyInputs[comment.comment_id] || ""}
                              onChange={(e) =>
                                setReplyInputs((prev) => ({
                                  ...prev,
                                  [comment.comment_id]: e.target.value,
                                }))
                              }
                            />
                            <button
                              className="save"
                              onClick={() => postReply(comment.comment_id)}
                              disabled={
                                !replyInputs[comment.comment_id]?.trim()
                              }
                            >
                              Post
                            </button>
                          </div>
                        )}

                        {/* Replies */}
                        <div className="replies-list-section">
                          {comment.replies?.map((reply) => (
                            <div
                              key={reply.comment_id}
                              className="comment-item comment-container reply-container"
                            >
                              <div className="profile-container">
                                <Link
                                  to={`/@${encodeURIComponent(reply.username)}`}
                                >
                                  <img
                                    src={`/profile_pics_icons/${reply.user_id}.jpg`}
                                    alt={`${reply.fullName}'s profile`}
                                    className="profile_icon"
                                    width="30"
                                    height="30"
                                    onError={(e) =>
                                    (e.target.src =
                                      "/profile_pics_icons/default.jpg")
                                    }
                                  />
                                </Link>
                              </div>

                              <div className="comment-details">
                                <div className="comment-header">
                                  <Link
                                    to={`/@${encodeURIComponent(
                                      reply.username
                                    )}`}
                                    className="comment-author"
                                  >
                                    {reply.fullName}
                                  </Link>
                                  <div className="time-delete-container">
                                    <span className="comment-time">
                                      {formatDateToRelativeOrShortDate(
                                        reply.created_at
                                      )}
                                    </span>
                                    {userId && reply.user_id === userId && (
                                      <button
                                        className="delete-button"
                                        onClick={() =>
                                          handleDeleteReplyClick(
                                            comment.comment_id,
                                            reply.comment_id
                                          )
                                        }
                                      >
                                        🗑️
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <p className="comment-content">
                                  {reply.content}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Load More Replies */}
                        {parseInt(comment.noOfReplies) >
                          (comment.replies?.length || 0) &&
                          hasMoreRepliesMap[comment.comment_id] !== false && (
                            <button
                              onClick={() => loadReplies(comment.comment_id)}
                              disabled={repliesLoadingState[comment.comment_id]}
                              className="view-more-replies-btn"
                              style={{
                                marginLeft: "52px",
                                marginBottom: "10px",
                              }}
                            >
                              {repliesLoadingState[comment.comment_id]
                                ? "Loading replies..."
                                : `View more replies (${(parseInt(comment.noOfReplies) || 0) -
                                (comment.replies?.length || 0)
                                } remaining)`}
                            </button>
                          )}
                      </div>
                    );
                  })
                )}
                {isLoadingMoreComments && comments.length > 0 && (
                  <p>Loading more comments...</p>
                )}
              </div>

              {/* Comment Input Footer */}
              <div className="comments-footer" style={{ backgroundColor: post.theme || "#333" }}>
                <img
                  className="profile_icon"
                  id="commenter_icon"
                  src={`/profile_pics_icons/${profileImage}`}
                  height="33"
                  width="33"
                  alt="Your profile picture"
                  loading="lazy"
                  onError={(e) =>
                    (e.target.src = "/profile_pics_icons/default.jpg")
                  }
                />
                <textarea
                  ref={commentRef}
                  className="fullTextbox"
                  id="shortComment"
                  placeholder="Type your comment here..."
                  value={newComment}
                  rows="1"
                  aria-label="Write a comment"
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      postComment();
                    }
                  }}
                />
                <button
                  id="postShortComment"
                  className="save"
                  aria-label="Post comment"
                  onClick={postComment}
                  disabled={!newComment.trim()}
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CommentsModal;
